import React from "react";
import {
  Select,
  MenuItem,
  ListItemText,
  SelectChangeEvent,
  InputLabel,
  FormControl,
  FormHelperText,
  OutlinedInput,
  Autocomplete,
  TextField,
} from "@mui/material";
import { CustomCheckbox } from "../StyledCheckbox";
import { RadioOption } from "../../../interface";
import { VariableSizeList } from "react-window";

interface MultiSelectProps {
  id: string;
  name?: string;
  value: string[] | string;
  options: RadioOption[];
  label?: string;
  showLabel?: boolean;
  useMuiLabel?: boolean;
  placeholder?: string;
  onChange: (event: SelectChangeEvent<string[] | string> | any) => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  testId?: string;
  className?: string;
  multiple?: boolean;
  isVirtualized?: boolean;
  isAutocomplete?: boolean;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  id,
  name,
  value,
  options,
  label,
  showLabel = true,
  useMuiLabel = false,
  placeholder = "Select options",
  onChange,
  error = false,
  helperText,
  disabled = false,
  testId,
  className,
  multiple = true,
  isVirtualized = false,
  isAutocomplete = false,
}) => {
  const isMulti = multiple;

  const isSelected = (optionValue: string) => {
    if (isMulti && Array.isArray(value)) {
      return value.includes(optionValue);
    }
    return value === optionValue;
  };

  const getLabelByValue = (val: string) => {
    const found = options.find((opt) => opt.value === val);
    return found ? found.label : val;
  };

  const renderValue = (selected: unknown) => {
    if (!selected || (Array.isArray(selected) && selected.length === 0)) {
      return useMuiLabel ? undefined : (
        <span style={{ color: "#aaa" }}>{placeholder}</span>
      );
    }
    return isMulti && Array.isArray(selected)
      ? selected.map(getLabelByValue).join(", ")
      : getLabelByValue(selected as string);
  };

  const handleOptionClick = (optionValue: string) => {
    if (isMulti) {
      const newValue =
        isSelected(optionValue) && Array.isArray(value)
          ? value.filter((val: string) => val !== optionValue)
          : [...(value as string[]), optionValue];
      onChange({ target: { name, value: newValue } } as SelectChangeEvent<
        string[]
      >);
    } else {
      onChange({
        target: { name, value: optionValue },
      } as SelectChangeEvent<string>);
    }
  };

  const getRowHeight = (index: number) => {
    const textLength = options[index].label.length;
    return textLength > 50 ? 60 : textLength > 30 ? 50 : 40;
  };

  const optionValues = options.map((opt) => opt.value);

  if (isAutocomplete) {
    return (
      <FormControl
        fullWidth
        error={error}
        disabled={disabled}
        className={className}
      >
        <Autocomplete
          data-testid={testId}
          multiple={isMulti}
          disableCloseOnSelect
          limitTags={4}
          options={optionValues}
          value={value as string[]}
          onChange={(_e, newValue) =>
            onChange({ target: { name, value: newValue } })
          }
          getOptionLabel={(option) => getLabelByValue(option)}
          isOptionEqualToValue={(option, val) => option === val}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <CustomCheckbox checked={selected} size="small" />
              {getLabelByValue(option)}
            </li>
          )}
          noOptionsText="No options"
          renderInput={(params) => {
            return (
              <TextField
                {...params}
                placeholder={placeholder}
                error={error}
                label={showLabel ? label : undefined}
                helperText={helperText}
                InputLabelProps={{
                  shrink: !!(Array.isArray(value) ? value.length : value),
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        maxHeight: "75px",
                        overflowY: "auto",
                      }}
                    >
                      {params.InputProps.startAdornment}
                    </div>
                  ),
                }}
              />
            );
          }}
          ListboxProps={{ style: { maxHeight: 250 } }}
          sx={{
            "&.MuiAutocomplete-hasPopupIcon": {
              ".MuiAutocomplete-inputRoot": {
                paddingRight: "9px !important",
              },
            },
            ".MuiAutocomplete-endAdornment": {
              top: "calc(100% - 40px)",
            },
          }}
        />
      </FormControl>
    );
  }

  return (
    <FormControl
      fullWidth
      error={error}
      disabled={disabled}
      className={className}
    >
      {showLabel && label && useMuiLabel && (
        <InputLabel id={`${id}-label`} htmlFor={id}>
          {label}
        </InputLabel>
      )}
      {showLabel && label && !useMuiLabel && (
        <label htmlFor={id} style={{ marginBottom: 4 }}>
          {label}
        </label>
      )}
      <Select
        id={id}
        data-testid={testId}
        name={name}
        MenuProps={{ disablePortal: true }}
        value={value}
        multiple={isMulti}
        displayEmpty={!useMuiLabel}
        labelId={useMuiLabel ? `${id}-label` : undefined}
        label={useMuiLabel ? label : undefined}
        renderValue={renderValue}
        error={error}
        disabled={disabled}
        input={
          useMuiLabel ? (
            <OutlinedInput
              label={label}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": { borderRadius: "2px" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#0B41CD",
                },
              }}
            />
          ) : undefined
        }
        sx={{
          borderRadius: "2px",
          "&.Mui-focused": {
            borderColor: "#0B41CD",
          },
        }}
      >
        {useMuiLabel && !isMulti && (
          <MenuItem value="" disabled>
            <em>{placeholder}</em>
          </MenuItem>
        )}
        {isVirtualized ? (
          <VariableSizeList
            height={Math.min(options.length, 8) * 40}
            itemCount={options.length}
            width="100%"
            itemData={options}
            itemSize={getRowHeight}
          >
            {({ index, style, data }) => {
              const option = data[index];
              return (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  style={style}
                  onClick={() => handleOptionClick(option.value)}
                >
                  {isMulti ? (
                    <CustomCheckbox
                      size="small"
                      checked={isSelected(option.value)}
                    />
                  ) : null}
                  <ListItemText
                    primary={option.label}
                    sx={{ textWrap: "auto" }}
                  />
                </MenuItem>
              );
            }}
          </VariableSizeList>
        ) : (
          options.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              onClick={() => handleOptionClick(option.value)}
            >
              {isMulti ? (
                <CustomCheckbox
                  size="small"
                  checked={isSelected(option.value)}
                />
              ) : null}
              <ListItemText primary={option.label} />
            </MenuItem>
          ))
        )}
      </Select>
      {helperText && (
        <FormHelperText sx={{ ml: 0 }}>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
};

export default MultiSelect;
